/**
 * Apartment hero carousel.
 *
 * Initializes the Swiper carousel rendered by custom-apartment/hero-carousel.blade.php and
 * the fullscreen modal carousel it opens. The modal is also opened by the hero anchor links:
 *
 * - Kuvat     → images, first slide
 * - Pohjakuva → images, floor plan slide
 * - Videot    → videos only
 */

import Swiper from 'swiper';
import {Navigation, Pagination, Keyboard} from 'swiper/modules';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled]):not([tabindex="-1"])',
  '[href]',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function youtubeThumbnail(id) {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

function youtubeEmbed(id) {
  return `https://www.youtube.com/embed/${id}?autoplay=1`;
}

function chevron(direction) {
  const path = direction === 'prev' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;
}

/**
 * Split the carousel items into the groups the modal can show.
 *
 * Returns the groups plus, for every hero slide, the group and the index within it.
 */
function groupItems(items) {
  const groups = {images: [], videos: []};
  const positions = items.map((item) => {
    const group = item.type === 'video' ? 'videos' : 'images';
    const index = groups[group].length;

    groups[group].push(item);

    return {group, index};
  });

  return {groups, positions};
}

function createModal(labels) {
  const modal = document.createElement('div');

  modal.className = 'apartment-hero-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.tabIndex = -1;
  modal.hidden = true;

  modal.innerHTML = `
    <div class="apartment-hero-modal__backdrop" aria-hidden="true"></div>
    <div class="swiper apartment-hero-modal__swiper" data-swiper-skip-global>
      <div class="swiper-wrapper"></div>
      <button type="button" class="apartment-hero-modal__nav apartment-hero-modal__nav--prev"
        aria-label="${labels.previous || ''}">${chevron('prev')}</button>
      <button type="button" class="apartment-hero-modal__nav apartment-hero-modal__nav--next"
        aria-label="${labels.next || ''}">${chevron('next')}</button>
      <div class="swiper-pagination apartment-hero-modal__pagination"></div>
    </div>
    <button type="button" class="apartment-hero-modal__close" aria-label="${labels.close || ''}">
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  `;

  document.body.append(modal);

  return modal;
}

function slideMarkup(item, labels) {
  if (item.type === 'video') {
    return `
      <div class="swiper-slide apartment-hero-modal__slide">
        <div class="apartment-hero-modal__video">
          <iframe class="apartment-hero-modal__iframe" src="${youtubeEmbed(item.id)}"
            title="${labels.video || ''}" allowfullscreen
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"></iframe>
        </div>
      </div>
    `;
  }

  const caption = item.caption
    ? `<p class="apartment-hero-modal__caption">${item.caption}</p>`
    : '';

  return `
    <div class="swiper-slide apartment-hero-modal__slide">
      <img class="apartment-hero-modal__image" src="${item.large}" alt="" loading="lazy">
      ${caption}
    </div>
  `;
}

/**
 * Show portrait images whole instead of cropped to the 16/9 stage.
 *
 * Floor plans are the common case, but any portrait image loses too much to the
 * crop. The orientation is only known once the image has loaded.
 */
function containPortraitImages(root) {
  root.querySelectorAll('.apartment-hero__image').forEach((image) => {
    const markOrientation = () => {
      image.classList.toggle(
        'apartment-hero__image--contain',
        image.naturalHeight > image.naturalWidth
      );
    };

    if (image.complete && image.naturalWidth) {
      markOrientation();
    } else {
      image.addEventListener('load', markOrientation, {once: true});
    }
  });
}

function init() {
  const container = document.querySelector('[data-apartment-hero-carousel]');
  if (!container) {
    return;
  }

  let data;
  try {
    data = JSON.parse(container.dataset.carousel);
  } catch (error) {
    return;
  }

  const items = data?.items || [];
  const labels = data?.labels || {};
  if (!items.length) {
    return;
  }

  const {groups, positions} = groupItems(items);

  /** ---------------
   *  Hero carousel
   *  --------------- */

  containPortraitImages(container);

  const heroEl = container.querySelector('.apartment-hero__swiper');
  if (heroEl) {
    new Swiper(heroEl, {
      modules: [Navigation, Pagination, Keyboard],
      slidesPerView: 1,
      loop: items.length > 1,
      grabCursor: true,
      navigation: {
        prevEl: container.querySelector('.apartment-hero__nav--prev'),
        nextEl: container.querySelector('.apartment-hero__nav--next'),
      },
      pagination: {
        el: container.querySelector('.apartment-hero__pagination'),
        type: 'fraction',
        // Swiper's default fraction pads the separator with spaces ("1 / 12").
        renderFraction: (currentClass, totalClass) =>
          `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    });
  }

  /** ---------------
   *  Modal carousel
   *  --------------- */

  let modal = null;
  let modalSwiper = null;
  let lastFocused = null;

  const closeModal = () => {
    if (!modal || modal.hidden) {
      return;
    }

    if (modalSwiper) {
      modalSwiper.destroy(true, true);
      modalSwiper = null;
    }

    // Emptying the wrapper also stops any playing video.
    modal.querySelector('.swiper-wrapper').replaceChildren();
    modal.hidden = true;
    document.documentElement.style.overflowY = '';
    lastFocused?.focus();
  };

  const trapFocus = (event) => {
    const focusable = [...modal.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  };

  const ensureModal = () => {
    if (modal) {
      return modal;
    }

    modal = createModal(labels);

    // The backdrop does not close the modal, the close button and Esc do.
    modal.querySelector('.apartment-hero-modal__close').addEventListener('click', closeModal);

    // On the document, so Esc still closes the modal after a click on the
    // backdrop or on a slide has moved focus out of the dialog.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        trapFocus(event);
      }
    });

    // Keeps the focus trap intact when clicking a non-focusable part of the modal.
    modal.addEventListener('click', (event) => {
      if (!event.target.closest(FOCUSABLE_SELECTOR)) {
        modal.focus();
      }
    });

    return modal;
  };

  const openModal = (group, index) => {
    const groupItemList = groups[group] || [];
    if (!groupItemList.length) {
      return;
    }

    ensureModal();
    closeModal();

    lastFocused = document.activeElement;

    modal.querySelector('.swiper-wrapper').innerHTML = groupItemList
      .map((item) => slideMarkup(item, labels))
      .join('');
    modal.hidden = false;
    document.documentElement.style.overflowY = 'hidden';

    modalSwiper = new Swiper(modal.querySelector('.apartment-hero-modal__swiper'), {
      modules: [Navigation, Pagination, Keyboard],
      slidesPerView: 1,
      initialSlide: Math.max(0, Math.min(index, groupItemList.length - 1)),
      loop: groupItemList.length > 1,
      grabCursor: true,
      navigation: {
        prevEl: modal.querySelector('.apartment-hero-modal__nav--prev'),
        nextEl: modal.querySelector('.apartment-hero-modal__nav--next'),
      },
      pagination: {
        el: modal.querySelector('.apartment-hero-modal__pagination'),
        type: 'fraction',
      },
      keyboard: {
        enabled: true,
      },
    });

    modal.querySelector('.apartment-hero-modal__close').focus();
  };

  // Clicking a hero slide opens it in the modal.
  container.addEventListener('click', (event) => {
    const slide = event.target.closest('[data-apartment-hero-slide]');
    if (!slide) {
      return;
    }

    const position = positions[Number(slide.dataset.apartmentHeroSlide)];
    if (position) {
      openModal(position.group, position.index);
    }
  });

  // Anchor links.
  document.querySelectorAll('[data-apartment-hero-open]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const target = trigger.dataset.apartmentHeroOpen;

      if (target === 'videos') {
        openModal('videos', 0);
        return;
      }

      const index = target === 'floorplan'
        ? Math.max(0, groups.images.findIndex((item) => item.floorplan))
        : 0;

      openModal('images', index);
    });
  });
}

export default {init};
